// Utilidades de hashing perceptual de imágenes (dHash) para detección de
// duplicados a nivel de frontend. Compartidas por las vistas de imágenes y de
// preguntas repetidas.

// Lado del hash perceptual (dHash). Se compara cada píxel con su vecino derecho
// sobre una rejilla (HASH_SIZE+1) x HASH_SIZE en escala de grises => 64 bits.
const HASH_SIZE = 8;

// Tiempo máximo de espera para cargar una imagen antes de descartarla.
const LOAD_TIMEOUT_MS = 15000;

/**
 * Carga una imagen pidiendo acceso CORS (necesario para leer sus píxeles).
 * El atributo crossOrigin debe fijarse ANTES del src.
 */
export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error('timeout'));
      }
    }, LOAD_TIMEOUT_MS);
    img.onload = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        resolve(img);
      }
    };
    img.onerror = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        reject(new Error('load error'));
      }
    };
    img.src = url;
  });
}

/**
 * Calcula el dHash (difference hash) de una imagen ya cargada, devolviendo una
 * cadena binaria de HASH_SIZE*HASH_SIZE bits. Lanza si el canvas está "tainted"
 * (imagen sin CORS).
 */
export function computeDHash(img) {
  const width = HASH_SIZE + 1;
  const height = HASH_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);

  // Luminancia (escala de grises) por píxel.
  const gray = new Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
  }

  // Cada bit: ¿el píxel es más oscuro que su vecino de la derecha?
  let hash = '';
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < HASH_SIZE; x++) {
      hash += gray[y * width + x] < gray[y * width + x + 1] ? '1' : '0';
    }
  }
  return hash;
}

/**
 * Carga y hashea una imagen desde su URL. Devuelve la cadena del hash o null si
 * no se pudo leer (p. ej. CORS o URL expirada).
 */
export async function hashImageUrl(url) {
  try {
    const img = await loadImage(url);
    return computeDHash(img);
  } catch (error) {
    return null;
  }
}

/**
 * Distancia de Hamming normalizada (0-1) entre dos cadenas binarias de igual
 * longitud. 0 = idénticas.
 */
export function hammingNormalized(a, b) {
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) diff += 1;
  }
  return diff / a.length;
}

/**
 * Recorre `items` aplicando `fn` con un máximo de `limit` tareas en paralelo.
 */
export async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await fn(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}
